import {
  ContractTermsData,
  ContractTermsForm,
} from "../Partnership/ContractTermsForm";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Info, Plus, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { useConvexMutation } from "@convex-dev/react-query";
import { useMutation } from "@tanstack/react-query";
import { useQuery } from "convex/react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";

interface InviteCreatorData {
  campaignId: Id<"campaigns">;
  emails: string[];
  startDate: number;
  endDate: number;
  currency: "usd" | "eur" | "gbp";
  requiredVideos: {
    count: number;
    duration: number;
  };
  customMessage?: string;
  flatFee?: {
    perVideo?: number;
    timePeriod?: {
      amount: number;
      days: number;
    };
  };
  cpmRate?: {
    type: "totalViews" | "perVideo";
    threshold: number;
    rate: number;
    maxPayout?: number;
    cutoffPeriod: number;
  };
}

interface InviteCreatorDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  campaignId: Id<"campaigns">;
  maxEndDate?: number; // Maximum allowed end date (e.g., campaign end date)
}

export const InviteCreatorDialog = ({
  open,
  onOpenChange,
  campaignId,
  maxEndDate,
}: InviteCreatorDialogProps) => {
  const [emails, setEmails] = useState<string[]>([""]);
  const [customMessage, setCustomMessage] = useState("");
  const [contractData, setContractData] = useState<ContractTermsData>(() => {
    const startDate = Date.now();
    const endDate = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0,
    ).getTime(); // End of current month
    const contractDuration = Math.ceil(
      (endDate - startDate) / (1000 * 60 * 60 * 24),
    );

    return {
      startDate,
      endDate,
      currency: "usd" as const,
      requiredVideos: {
        count: 1,
        duration: contractDuration, // Use actual contract duration instead of hardcoded 30
      },
    };
  });
  const { toast } = useToast();

  // Fetch campaign data to check if it's a challenge campaign
  const campaign = useQuery(api.campaigns.getCampaign, {
    campaignId,
  });

  const { mutateAsync: inviteCreator, isPending } = useMutation({
    mutationFn: useConvexMutation(api.invites.inviteCreator),
  });

  const resetForm = () => {
    setEmails([""]);
    setCustomMessage("");
    const startDate = Date.now();
    const endDate = Date.now() + 90 * 24 * 60 * 60 * 1000;
    const contractDuration = Math.ceil(
      (endDate - startDate) / (1000 * 60 * 60 * 24),
    );

    setContractData({
      startDate,
      endDate,
      currency: "usd",
      requiredVideos: {
        count: 1,
        duration: contractDuration, // Use actual contract duration (90 days)
      },
    });
  };

  const addEmailField = () => {
    setEmails([...emails, ""]);
  };

  const removeEmailField = (index: number) => {
    if (emails.length > 1) {
      setEmails(emails.filter((_, i) => i !== index));
    }
  };

  const updateEmail = (index: number, value: string) => {
    const updatedEmails = [...emails];
    updatedEmails[index] = value;
    setEmails(updatedEmails);
  };

  const isChallengeCampaign = campaign?.type === "challenge";

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Filter out empty emails and validate
    const validEmails = emails.filter((email) => email.trim() !== "");

    if (validEmails.length === 0) {
      toast({
        title: "Error",
        description: "Please enter at least one email address.",
        variant: "destructive",
      });
      return;
    }

    // For challenge campaigns, we don't need contract terms validation
    if (!isChallengeCampaign) {
      if (
        !contractData.startDate ||
        !contractData.endDate ||
        !contractData.requiredVideos.count
      ) {
        toast({
          title: "Error",
          description:
            "Please fill in all required fields and at least one email.",
          variant: "destructive",
        });
        return;
      }

      // Validate that end date is not before start date
      if (new Date(contractData.endDate) <= new Date(contractData.startDate)) {
        toast({
          title: "Error",
          description: "End date must be after the start date.",
          variant: "destructive",
        });
        return;
      }

      // Validate that end date is not after campaign end date
      if (maxEndDate && contractData.endDate > maxEndDate) {
        toast({
          title: "Error",
          description: `End date cannot be after campaign end date (${new Date(maxEndDate).toLocaleDateString()}).`,
          variant: "destructive",
        });
        return;
      }
    } else {
      // For challenge campaigns, still validate dates
      if (!contractData.startDate || !contractData.endDate) {
        toast({
          title: "Error",
          description: "Please fill in start and end dates.",
          variant: "destructive",
        });
        return;
      }

      // Validate that end date is not before start date
      if (new Date(contractData.endDate) <= new Date(contractData.startDate)) {
        toast({
          title: "Error",
          description: "End date must be after the start date.",
          variant: "destructive",
        });
        return;
      }

      // Validate that end date is not after campaign end date
      if (maxEndDate && contractData.endDate > maxEndDate) {
        toast({
          title: "Error",
          description: `End date cannot be after campaign end date (${new Date(maxEndDate).toLocaleDateString()}).`,
          variant: "destructive",
        });
        return;
      }
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = validEmails.filter(
      (email) => !emailRegex.test(email.trim()),
    );

    if (invalidEmails.length > 0) {
      toast({
        title: "Error",
        description: "Please enter valid email addresses for all fields.",
        variant: "destructive",
      });
      return;
    }

    // Build the invitation data
    const inviteData: InviteCreatorData = {
      campaignId,
      emails: validEmails.map((email) => email.trim()),
      customMessage: customMessage || undefined,
      // Use actual contract data dates for both campaign types
      startDate: contractData.startDate,
      endDate: contractData.endDate,
      currency: isChallengeCampaign ? "usd" : contractData.currency,
      requiredVideos: isChallengeCampaign
        ? { count: 0, duration: 0 } // No required videos for challenge campaigns
        : contractData.requiredVideos,
      // No flat fee or CPM rate for challenge campaigns - they use predefined CPM
      ...(isChallengeCampaign
        ? {
            cpmRate: {
              type: "perVideo",
              threshold: 0,
              rate: campaign.predefinedCpm || 0,
              cutoffPeriod: 7,
            },
          }
        : {
            flatFee: contractData.flatFee,
            cpmRate: contractData.cpmRate
              ? {
                  ...contractData.cpmRate,
                  cutoffPeriod: contractData.cpmRate.cutoffPeriod || 10,
                }
              : undefined,
          }),
    };

    try {
      await inviteCreator(inviteData);

      toast({
        title: "Invites sent!",
        description: `${validEmails.length} creator invitation${validEmails.length > 1 ? "s have" : " has"} been sent successfully. You can review your creator invitations in the settings page.`,
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error",
        description: (error as { data?: string }).data
          ? (error as { data?: string }).data
          : "Failed to send invitation. Please try again.",
        variant: "destructive",
      });
    }
  };

  const getCurrencySymbol = (currency: string) => {
    switch (currency) {
      case "usd":
        return "$";
      case "eur":
        return "€";
      case "gbp":
        return "£";
      default:
        return "$";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Invite Creator{emails.filter((e) => e.trim()).length > 1 ? "s" : ""}
            {campaign?.name && ` to ${campaign.name}`}
          </DialogTitle>
          <DialogDescription>
            {isChallengeCampaign
              ? `Send invitations to creators for this open challenge campaign. Creators can start posting immediately when they accept and earn ${getCurrencySymbol("usd")}${campaign.predefinedCpm?.toFixed(2) || "0.00"} per 1,000 views.`
              : `Send partnership invitation${emails.filter((e) => e.trim()).length > 1 ? "s" : ""} to creator${emails.filter((e) => e.trim()).length > 1 ? "s" : ""} with pre-defined contract terms.`}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label htmlFor="emails">
                  Creator Email{emails.length > 1 ? "s" : ""} *
                </Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addEmailField}
                  className="text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Add Email
                </Button>
              </div>
              <div className="space-y-2">
                {emails.map((email, index) => (
                  <div key={index} className="flex gap-2">
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => updateEmail(index, e.target.value)}
                      placeholder={`creator${index > 0 ? index + 1 : ""}@example.com`}
                      required={index === 0}
                      className="flex-1"
                    />
                    {emails.length > 1 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeEmailField(index)}
                        className="px-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
              {emails.filter((e) => e.trim()).length > 1 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {emails.filter((e) => e.trim()).length} creator
                  {emails.filter((e) => e.trim()).length > 1 ? "s" : ""} will
                  receive the same invitation
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="message">Custom Message</Label>
              <Textarea
                id="message"
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder={
                  isChallengeCampaign
                    ? "Add a personal message about your challenge campaign..."
                    : "Add a personal message to the creator..."
                }
                rows={3}
              />
            </div>
          </div>

          {/* Challenge Campaign Info */}
          {isChallengeCampaign && campaign && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">
                Challenge Campaign Details
              </h3>

              <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-start space-x-2">
                  <Info className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-800 dark:text-green-300 mb-2">
                      Open Challenge Format
                    </h4>
                    <div className="space-y-2 text-sm text-green-700 dark:text-green-400">
                      <p>
                        <strong>CPM Reward:</strong> {getCurrencySymbol("usd")}
                        {campaign.predefinedCpm?.toFixed(2) || "0.00"} per 1,000
                        views
                      </p>
                      <p>
                        <strong>How it works:</strong> Creators can post as much
                        content as they want. Each post gets paid based on
                        actual views received.
                      </p>
                      {campaign.validationRequirements && (
                        <p>
                          <strong>Post Requirements:</strong>{" "}
                          {campaign.validationRequirements}
                        </p>
                      )}
                      <p>
                        <strong>Campaign Duration:</strong>{" "}
                        {campaign.endDate
                          ? `Until ${new Date(campaign.endDate).toLocaleDateString()}`
                          : "Ongoing"}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contract Period for Challenge Campaigns */}
              <div className="space-y-4">
                <h4 className="font-medium">Contract Period</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="startDate">Start Date *</Label>
                    <Input
                      id="startDate"
                      type="date"
                      value={
                        new Date(contractData.startDate)
                          .toISOString()
                          .split("T")[0]
                      }
                      onChange={(e) =>
                        setContractData({
                          ...contractData,
                          startDate: new Date(e.target.value).getTime(),
                        })
                      }
                      min={new Date().toISOString().split("T")[0]}
                    />
                  </div>
                  <div>
                    <Label htmlFor="endDate">End Date *</Label>
                    <Input
                      id="endDate"
                      type="date"
                      value={
                        new Date(contractData.endDate)
                          .toISOString()
                          .split("T")[0]
                      }
                      onChange={(e) =>
                        setContractData({
                          ...contractData,
                          endDate: new Date(e.target.value).getTime(),
                        })
                      }
                      min={
                        new Date(contractData.startDate)
                          .toISOString()
                          .split("T")[0]
                      }
                      max={
                        maxEndDate
                          ? new Date(maxEndDate).toISOString().split("T")[0]
                          : undefined
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Contract Terms - Only for non-challenge campaigns */}
          {!isChallengeCampaign && (
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Contract Terms</h3>
              <ContractTermsForm
                contractData={contractData}
                onContractDataChange={setContractData}
                showDateFields={true}
                maxEndDate={maxEndDate}
              />
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending
                ? "Sending..."
                : `Send Invitation${emails.filter((e) => e.trim()).length > 1 ? "s" : ""}`}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
